import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PortalShell } from "@/pages/ClientPortalShell";
import { Check, Loader2, ShieldCheck, Sparkles, Zap, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const TIER_ICON = {
  storage: ShieldCheck,
  creator: Sparkles,
  pro: Zap,
};

export default function Upgrade() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [links, setLinks] = useState({}); // { storage: {monthly, yearly}, ... }
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState("monthly"); // monthly | yearly
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/plans"),
      api.get("/subscriptions/me"),
      api.get("/subscriptions/payment-links").catch(() => ({ data: {} })),
    ])
      .then(([p, m, l]) => {
        setPlans(p.data.plans || []);
        setMe(m.data);
        setLinks(l.data || {});
      })
      .catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoading(false));
  }, []);

  const currentTierRank = useMemo(() => {
    const rank = { free: 0, storage: 1, creator: 2, pro: 3 };
    return rank[me?.tier || "free"] || 0;
  }, [me]);

  const linkFor = (plan) => {
    const entry = links[plan.tier] || {};
    return interval === "yearly" ? entry.yearly : entry.monthly;
  };

  const subscribe = (plan) => {
    if (checkingOut) return;
    const url = linkFor(plan);
    if (!url) {
      toast.error(
        interval === "yearly"
          ? "Annual plan not available yet — pick Monthly or contact support."
          : "Payment link not configured yet."
      );
      return;
    }
    setCheckingOut(plan.tier);
    // Redirect to prebuilt Stripe Payment Link (client_reference_id already appended)
    window.location.href = url;
  };

  const openPortal = async () => {
    try {
      const r = await api.post("/subscriptions/portal");
      window.location.href = r.data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Portal not available");
    }
  };

  return (
    <PortalShell>
      <div className="border-l-4 border-orange-600 pl-5 mb-8" data-testid="upgrade-header">
        <div className="label-tiny mb-2">Choose your plan</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">
          Level up your rodeo studio
        </h1>
        <p className="text-stone-400 mt-3 max-w-2xl">
          Every plan is billed in CAD, cancel any time from the Stripe portal. Prices below and inside
          Stripe are the same, no hidden fees.
        </p>
      </div>

      {me && me.tier !== "free" && (
        <div className="border border-emerald-700 bg-emerald-600/10 p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="current-plan-banner">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Current plan</div>
            <div className="font-display uppercase font-bold text-lg">
              Rodeo {me.tier.charAt(0).toUpperCase() + me.tier.slice(1)}
              {me.status === "past_due" && <span className="text-rose-400 ml-2 text-xs">Payment past due</span>}
              {me.cancel_at_period_end && <span className="text-amber-400 ml-2 text-xs">Cancels at period end</span>}
            </div>
            {me.period_end && (
              <div className="text-xs text-stone-400 mt-0.5">
                Renews {me.period_end.slice(0, 10)}
              </div>
            )}
          </div>
          <button onClick={openPortal} className="btn-secondary inline-flex items-center gap-2 text-xs" data-testid="manage-subscription">
            Manage subscription <ExternalLink size={11} />
          </button>
        </div>
      )}

      {/* Interval toggle */}
      <div className="inline-flex border border-stone-800 mb-8" data-testid="interval-toggle">
        <button
          onClick={() => setInterval("monthly")}
          className={`px-4 py-2 text-xs uppercase tracking-widest ${
            interval === "monthly" ? "bg-orange-600 text-white" : "text-stone-400 hover:text-stone-100"
          }`}
          data-testid="interval-monthly"
        >
          Monthly
        </button>
        <button
          onClick={() => setInterval("yearly")}
          className={`px-4 py-2 text-xs uppercase tracking-widest ${
            interval === "yearly" ? "bg-orange-600 text-white" : "text-stone-400 hover:text-stone-100"
          }`}
          data-testid="interval-yearly"
        >
          Yearly <span className="text-[10px] ml-1 opacity-80">save ~17%</span>
        </button>
      </div>

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading plans…</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-5" data-testid="plans-grid">
          {plans.map((plan) => {
            const price = interval === "yearly" ? plan.yearly_price : plan.monthly_price;
            const isCurrent = me?.tier === plan.tier;
            const canUpgrade = { free: 0, storage: 1, creator: 2, pro: 3 }[plan.tier] > currentTierRank;
            const hasLink = Boolean(linkFor(plan));
            const Icon = TIER_ICON[plan.tier] || Sparkles;
            const featured = plan.tier === "creator";
            return (
              <div
                key={plan.tier}
                className={`border ${featured ? "border-orange-600" : "border-stone-800"} p-6 flex flex-col relative`}
                data-testid={`plan-${plan.tier}`}
              >
                {featured && (
                  <div className="absolute -top-3 left-6 bg-orange-600 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-mono-tc">
                    Most popular
                  </div>
                )}
                <div className="inline-flex items-center gap-2 mb-4">
                  <Icon size={18} className="text-orange-500" />
                  <div className="font-display uppercase font-bold text-xl">{plan.name}</div>
                </div>
                <div className="mb-4">
                  <div className="font-mono-tc text-4xl font-bold">
                    ${price}
                    <span className="text-base font-normal text-stone-500 ml-1">
                      /{interval === "yearly" ? "yr" : "mo"}
                    </span>
                  </div>
                  {interval === "yearly" && (
                    <div className="text-[11px] text-emerald-400 mt-1">
                      = ${(price / 12).toFixed(2)}/mo billed annually
                    </div>
                  )}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-300">
                      <Check size={13} className="text-orange-500 mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="border border-emerald-700 bg-emerald-600/10 text-emerald-400 text-center py-2 text-xs uppercase tracking-widest" data-testid={`plan-current-${plan.tier}`}>
                    <Check size={12} className="inline mr-1" /> Your plan
                  </div>
                ) : (
                  <button
                    onClick={() => subscribe(plan)}
                    disabled={!canUpgrade || checkingOut === plan.tier || !hasLink}
                    className={`btn-primary w-full inline-flex items-center justify-center gap-2 text-sm py-3 ${(!canUpgrade || !hasLink) ? "opacity-50" : ""}`}
                    data-testid={`plan-subscribe-${plan.tier}`}
                  >
                    {checkingOut === plan.tier ? (
                      <><Loader2 size={13} className="animate-spin" /> Redirecting…</>
                    ) : !hasLink && canUpgrade ? (
                      <>{interval === "yearly" ? "Annual coming soon" : "Unavailable"}</>
                    ) : canUpgrade ? (
                      <>Subscribe to {plan.name}</>
                    ) : (
                      <>Downgrade via portal</>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-10 text-[11px] text-stone-500 max-w-2xl">
        Buying clips from Rodeo Cut Studio does not require a subscription. Anyone with a login can view
        shared galleries and buy their runs. Subscriptions unlock uploading, cutting, reel building, and
        studio tools for your own footage.
      </div>
    </PortalShell>
  );
}
